// WebGL2 コンテキストの生成と喪失/復旧の扱い。

export interface GLContextHandle {
  gl: WebGL2RenderingContext
  canvas: HTMLCanvasElement
  maxTextureSize: number
  /** コンテキスト喪失中は true。描画をスキップする */
  lost: boolean
  onLost: (fn: () => void) => void
  onRestored: (fn: () => void) => void
  dispose: () => void
}

export function isWebGL2Supported(): boolean {
  try {
    const c = document.createElement('canvas')
    const gl = c.getContext('webgl2')
    if (!gl) return false
    gl.getExtension('WEBGL_lose_context')?.loseContext()
    return true
  } catch {
    return false
  }
}

export function createGLContext(canvas: HTMLCanvasElement): GLContextHandle | null {
  const gl = canvas.getContext('webgl2', {
    alpha: false,
    antialias: false,
    depth: false,
    stencil: false,
    premultipliedAlpha: true,
    preserveDrawingBuffer: false,
    powerPreference: 'high-performance'
  })
  if (!gl) return null
  const lostFns: (() => void)[] = []
  const restoredFns: (() => void)[] = []
  const handle: GLContextHandle = {
    gl,
    canvas,
    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
    lost: false,
    onLost: fn => lostFns.push(fn),
    onRestored: fn => restoredFns.push(fn),
    dispose: () => {
      canvas.removeEventListener('webglcontextlost', onLost)
      canvas.removeEventListener('webglcontextrestored', onRestored)
    }
  }
  const onLost = (e: Event) => {
    e.preventDefault()
    handle.lost = true
    for (const f of lostFns) f()
  }
  const onRestored = () => {
    handle.lost = false
    for (const f of restoredFns) f()
  }
  canvas.addEventListener('webglcontextlost', onLost)
  canvas.addEventListener('webglcontextrestored', onRestored)
  return handle
}
